import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Calendar } from '../components/ui/calendar';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/ui/button';
import { 
  Clock,
  Flame,
  Dumbbell,
  CalendarDays,
  TrendingUp,
  Trash2,
  Loader2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export const HistoryPage = () => {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  // Tracks the log id currently mid-delete so we can disable just that row's
  // button (and show a tiny spinner) instead of locking the whole page.
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await api().get('/logs');
        setLogs(res.data);
      } catch {
        // silent
      }
      setLoading(false);
    };
    fetchLogs();
  }, [api]);

  const handleDelete = async (log) => {
    const confirmMsg = `Delete log "${log.workout_title}" from ${new Date(log.completed_at).toLocaleDateString()}?\n\nThis can't be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setDeletingId(log.id);
    try {
      await api().delete(`/logs/${log.id}`);
      setLogs((prev) => prev.filter((l) => l.id !== log.id));
      toast.success('Log deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete log');
    } finally {
      setDeletingId(null);
    }
  };

  // Get dates with workouts for calendar highlighting
  const workoutDates = logs.map(log => new Date(log.completed_at).toDateString());
  
  // Filter logs for selected date
  const selectedDateLogs = logs.filter(
    log => new Date(log.completed_at).toDateString() === selectedDate?.toDateString()
  );

  // Stats
  const totalWorkouts = logs.length;
  const totalMinutes = logs.reduce((acc, log) => acc + log.duration_minutes, 0);
  const totalCalories = logs.reduce((acc, log) => acc + log.calories_burned, 0);

  return (
    <div className="min-h-screen bg-black" data-testid="history-page">
      <NavBar title="History" />

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
        >
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-2">
            Workout <span className="text-primary">History</span>
          </h2>
          <p className="text-muted-foreground text-lg">Track your progress over time</p>
        </motion.div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <Dumbbell className="h-6 w-6 text-primary" />
              <span className="text-muted-foreground text-sm uppercase tracking-wider">Total Workouts</span>
            </div>
            <p className="font-heading text-4xl text-white">{totalWorkouts}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock className="h-6 w-6 text-primary" />
              <span className="text-muted-foreground text-sm uppercase tracking-wider">Total Time</span>
            </div>
            <p className="font-heading text-4xl text-white">{totalMinutes} <span className="text-xl">min</span></p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-border p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <Flame className="h-6 w-6 text-primary" />
              <span className="text-muted-foreground text-sm uppercase tracking-wider">Calories Burned</span>
            </div>
            <p className="font-heading text-4xl text-white">{totalCalories.toLocaleString()}</p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calendar */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card border border-border p-6 flex flex-col items-center"
          >
            <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-6 flex items-center gap-3 w-full">
              <CalendarDays className="h-6 w-6 text-primary" />
              Activity Calendar
            </h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              modifiers={{
                workout: (date) => workoutDates.includes(date.toDateString())
              }}
              modifiersStyles={{
                workout: { backgroundColor: 'rgba(255, 215, 0, 0.2)', color: '#FFD700', fontWeight: 'bold' }
              }}
              className="rounded-none border-border"
            />
          </motion.div>

          {/* Selected Date Logs */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-6 flex items-center gap-3">
              <TrendingUp className="h-6 w-6 text-primary" />
              {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h3>
            
            {selectedDateLogs.length > 0 ? (
              <div className="space-y-4">
                {selectedDateLogs.map((log) => (
                  <div
                    key={log.id}
                    className="bg-card border border-border p-6 relative"
                    data-testid={`history-log-card-${log.id}`}
                  >
                    <button
                      onClick={() => handleDelete(log)}
                      disabled={deletingId === log.id}
                      className="absolute top-3 right-3 p-2 rounded-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                      aria-label="Delete log"
                      data-testid={`history-log-delete-${log.id}`}
                    >
                      {deletingId === log.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                    <h4 className="font-heading text-xl text-white uppercase tracking-wider mb-3 pr-10">{log.workout_title}</h4>
                    <div className="flex items-center gap-6 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-primary" />
                        <span>{log.duration_minutes} min</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Flame className="h-4 w-4 text-primary" />
                        <span>{log.calories_burned} cal</span>
                      </div>
                    </div>
                    {log.notes && (
                      <p className="mt-3 text-muted-foreground text-sm border-t border-border pt-3">{log.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-card border border-border p-8 text-center">
                <CalendarDays className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No workouts on this day</p>
              </div>
            )}

            {/* Recent Activity */}
            <h3 className="font-heading text-2xl text-white uppercase tracking-wider mt-10 mb-6">Recent Activity</h3>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {logs.slice(0, 10).map((log) => (
                <div
                  key={log.id}
                  className="bg-card border border-border p-4 flex items-center justify-between gap-3"
                  data-testid={`history-recent-row-${log.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium truncate">{log.workout_title}</p>
                    <p className="text-muted-foreground text-sm">
                      {log.duration_minutes} min · {log.calories_burned} cal
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-muted-foreground text-sm">
                      {new Date(log.completed_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => handleDelete(log)}
                      disabled={deletingId === log.id}
                      className="p-1.5 rounded-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                      aria-label="Delete log"
                      data-testid={`history-recent-delete-${log.id}`}
                    >
                      {deletingId === log.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};
